export interface Plant {
  plant_id?: string;
  common_name: string;
  scientific_name: string;
  family: string;
  genus: string;
  image_url?: string;
  tags?: string[];
}

export interface GardenObject {
  object_id: string;
  name: string;
  type: string;
  category: string;
  image_url?: string;
  tags?: string[];
}

export interface Company {
  company_id: string;
  name: string;
  description?: string;
  contact_name?: string; // For backward compatibility
  contact_email?: string;
  contact_phone?: string;
  website?: string;
  logo?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    country?: string;
  };
  service_areas?: string[];
  business_hours?: {
    monday?: string;
    tuesday?: string;
    wednesday?: string;
    thursday?: string;
    friday?: string;
    saturday?: string;
    sunday?: string;
  };
  timestamps?: {
    created_at?: string;
    updated_at?: string;
  };
}

export interface InventoryItem {
  inventory_id?: string;
  name?: string;
  type: string;
  quantity: number | string;
  selling_price: number | string;
  unit_cost?: number | string;
  supplier?: string;
  restock_threshold?: number | string;
  low_stock_threshold?: number | string;
  location_in_warehouse?: string;
  status?: string;
  company_id?: string;
  item_id?: string; // Unique identifier for the inventory item (sort key)
  timestamps?: any;
  // Enriched data from backend
  plant_data?: Plant; // Full plant data when type is "plant"
  object_data?: GardenObject; // Full object data when type is "object"
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// API base URL - replace with your actual API endpoint
// Use import.meta.env for Vite projects instead of process.env
// First, let's fix the unused API_BASE_URL
// Comment it out or remove it if not needed

// Mock API service functions
export const mockApiCall = <T>(
  data: T,
  delay = 500
): Promise<ApiResponse<T>> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        data,
      });
    }, delay);
  });
};

export interface PinLocation {
  latitude: number;
  longitude: number;
  address?: string;
  zoom?: number;
  bearing?: number;
  pitch?: number;
  company?: Company;
}

export interface PlacedObject {
  id: string;
  objectId: string; // Corresponds to CatalogObject id
  name: string;
  type: "plant" | "object";
  coordinates: {
    latitude: number;
    longitude: number;
  };
  rotation: number;
  scale: number;
}

export interface BoundaryPoint {
  id: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  order: number;
}

export type ActivePanelType = "object" | "boundary";

export interface ProjectData {
  id: string;
  name: string;
  description?: string;
  company_id: string;
  location: PinLocation;
  objects: PlacedObject[];
  boundaryPoints?: BoundaryPoint[];
  lastModified: string;
  status: "active" | "completed" | "archived";
  activePanel: ActivePanelType;
}

export interface CatalogObject {
  id: string;
  name: string;
  type: "plant" | "object";
  description: string;
  category: string;
  model_3d: string; // URL to the 3D model
  // Inventory information
  inventory_id?: string;
  quantity?: number;
  status?: string;
  unit_cost?: number;
  customer_price?: number;
  supplier?: string;
  location_in_warehouse?: string;
}

// Image generation function that calls the real API endpoint
export const generateImage = async (
  prompt: string,
  _itemId?: string,
  _itemType?: "plant" | "object"
): Promise<ApiResponse<string>> => {
  // Always use mock in browser for now until API is deployed
  return mockGenerateImage(prompt);

  // Real implementation for when the API is deployed:
  /*
  try {
    // Generate a temporary ID if we don't have a real one yet
    const id = itemId || `temp-${Date.now()}`;
    
    // Determine if this is a plant or object based on the prompt if not specified
    const type = itemType || (prompt.toLowerCase().includes('plant') ? 'plant' : 'object');
    
    const response = await fetch(`${API_BASE_URL}/generate-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        prompt: prompt,
        item_id: id,
        type: type
      })
    });
    
    const data = await response.json();
    
    if (data.success && data.data) {
      return {
        success: true,
        data: data.data.image_url
      };
    } else {
      console.error('Image generation API error:', data.error);
      return {
        success: false,
        error: data.error || 'Failed to generate image'
      };
    }
  } catch (err) {
    console.error('Error calling image generation API:', err);
    return {
      success: false,
      error: 'Error connecting to image generation service'
    };
  }
  */
};

// Mock image generation function for development
const mockGenerateImage = (_prompt: string): Promise<ApiResponse<string>> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        data: "https://via.placeholder.com/512x512.png?text=AI-Generated+Image",
      });
    }, 1500);
  });
};
